/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useCallback, useEffect, useRef, useState} from 'react'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useTranslation} from 'react-i18next'

import {openModal} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import {toast} from 'utils/toastUtils'

import {CompaniesAction} from 'store/companies/CompaniesAction'
import {CompanySellerAssignmentError} from 'utils/companies/CompanySellerAssignmentError'
import {ModalSize} from 'types/enums/ModalSize'
import {Company} from 'types/entities/Company'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {CompanyBasicInputs, useCompanyBasicSchema} from 'components/Companies/schemas/useCompanyBasicSchema'
import {CompanyFormProps} from 'types/entities/specific/CompanyFormTypes'
import {useDebouncedCallback} from 'utils/hooks/useDebounce'
import {
	employeeSizeOptions,
	getAutoBusinessCategoryForBranchType,
	mergeCompanyAfterSave,
	pruneEmpty,
	useCompanyBranchTypeOptions,
	useGetBusinessCategories,
} from 'utils/companyUtils'
import {normalizeCompanyOrigin} from 'utils/companies/companyOriginUtils'
import {filterDuplicatesExcludingCurrent, hasDuplicateCheckInput, normalizeEmployeeSize, trimOrFallback} from 'components/Companies/forms/CompanyBasicFormUtils'
import {useDuplicateWarning, useFormUIState, useSelectableField} from 'utils/hooks/useCompanyFormUI'
import {getSaveAndCloseLabel} from 'utils/utility'
import MergeCompaniesModal from 'components/Companies/Merge/MergeCompaniesModal'
import type {UiSelectOption} from 'components/Ui/UiSelectWithGraphics'
import useMappedSelectOptions from 'components/Companies/hooks/useMappedSelectOptions'

export interface UseCompanyBasicFormResult {
    form: ReturnType<typeof useForm<CompanyBasicInputs>>
    isSubmitting: boolean
    setIsSubmitting: (value: boolean) => void
    showPopover: boolean
    handleMouseEnter: () => void
    handleMouseLeave: () => void
    parentCompanyQuery: string
    setParentCompanyQuery: (value: string) => void
    parentCompany: Company | null
    setParentCompany: React.Dispatch<React.SetStateAction<Company | null>>
    duplicates: Company[]
    isOpeningMergeModal: boolean
    notesFontSize: number
    setNotesFontSize: React.Dispatch<React.SetStateAction<number>>
    businessCategoryField: ReturnType<typeof useSelectableField>
    branchTypeField: ReturnType<typeof useSelectableField>
    businessCategoryOptionsUi: UiSelectOption[]
    businessBranchTypeOptionsUi: UiSelectOption[]
    employeeSizeOptionsUi: UiSelectOption[]
    handleOpenMergeModal: () => void
    onSubmit: (data: Partial<Company>, shouldClose?: boolean) => Promise<void>
    onError: (error: unknown) => void
    saveAndCloseText: string
    cleanupDuplicateWarning: () => void
}

export const useCompanyBasicForm = ({
    onChange,
    isInEditModal,
    showSaveAndClose,
}: Pick<CompanyFormProps, 'onChange' | 'isInEditModal' | 'showSaveAndClose'>): UseCompanyBasicFormResult => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const company = useTypedSelector((state) => state.companiesState.selectedCompany)

    const { isSubmitting, setIsSubmitting } = useFormUIState()
    const { showPopover, handleMouseEnter, handleMouseLeave, cleanup } = useDuplicateWarning()
    const businessCategoryField = useSelectableField(company?.business_category || null)
    const branchTypeField = useSelectableField(company?.type || CompanyBranchType.Branch)
	const seller_id = useTypedSelector((state) => state.sessionState.session?.uid) || null

    const [parentCompanyQuery, setParentCompanyQuery] = useState('')
    const [parentCompany, setParentCompany] = useState<Company | null>(company?.parent_company || null)
    const [duplicates, setDuplicates] = useState<Company[]>([])
    const [isOpeningMergeModal, setIsOpeningMergeModal] = useState(false)
    const [notesFontSize, setNotesFontSize] = useState(12)

    const { businessCategories } = useGetBusinessCategories()
    const companyBasicSchema = useCompanyBasicSchema()
    const { options } = useCompanyBranchTypeOptions()

    const businessCategoryOptionsUi = useMappedSelectOptions(businessCategories)
    const businessBranchTypeOptionsUi = useMappedSelectOptions(options)
    const employeeSizeOptionsUi = useMappedSelectOptions(employeeSizeOptions)

    const form = useForm<CompanyBasicInputs>({
        resolver: zodResolver(companyBasicSchema),
        defaultValues: {
            name: company?.name || '',
            cin: company?.cin || '',
            vat_number: company?.vat_number || '',
            notes: company?.notes || '',
            referral_id:
                company?.referral_id != null ? String(company.referral_id) : '',
            website: company?.website || '',
            branch: company?.branch || null,
            type: company?.type || CompanyBranchType.Branch,
            parent_company_id: company?.parent_company_id || null,
            business_category: company?.business_category || undefined,
            employee_size: company?.employee_size == null || company.employee_size === ''
                ? null : String(company.employee_size),
            origin: company?.origin || '',
            tags: company?.tags || [],
            seller_id: company?.id ? (company.seller_id ?? null) : seller_id,
            first_name: company?.first_name || '',
            last_name: company?.last_name || '',
            email: company?.email || '',
            phone: company?.phone || '',
        },
    })

    const { reset, setValue, watch, getValues, setError } = form

    const companyName = watch('name')
    const cin = watch('cin')
    const vat = watch('vat_number')

    const checkDuplicates = useDebouncedCallback(
        async (name?: string, regNum?: string, vatNum?: string, isManual?: boolean) => {
            const hasEnoughInfo = hasDuplicateCheckInput(name, regNum, vatNum)

            if (hasEnoughInfo) {
                try {
                    const results = await dispatch(
                        CompaniesAction.fetchCheckDuplicates({
                            name,
                            registration_number: regNum,
                        })
                    )
                    const filtered = filterDuplicatesExcludingCurrent(results, company?.id)
                    setDuplicates(filtered)

                    if (filtered.length > 0 && isManual) {
                        toast.warning(t('A company with this name or information already exists'))
                    }
                } catch (error) {
                    console.error('Failed to check duplicates', error)
                }
            } else {
                setDuplicates([])
            }
        },
        500
    )

    const { setValue: setBusinessCategory } = businessCategoryField
    const { setValue: setBranchType, value: branchTypeValue } = branchTypeField
    const syncedCompanyIdRef = useRef<number | null | undefined>(undefined)

    useEffect(() => {
        const companyId = company?.id ?? null
        // Re-hydrate only when switching companies — never overwrite in-progress edits.
        if (syncedCompanyIdRef.current === companyId) {
            return
        }
        syncedCompanyIdRef.current = companyId
        // A create response establishes its ID while the user may still be editing.
        // Preserve that draft until the whole save (including owner assignment) finishes.
        if (isSubmitting) return

        const nextCategory = company?.business_category ?? null
        const nextSize = company?.employee_size == null || company.employee_size === ''
            ? null : String(company.employee_size)
        const nextOrigin = company?.origin ?? ''
        const nextTags = company?.tags ?? []

        reset({
            name: company?.name ?? '',
            cin: company?.cin ?? '',
            vat_number: company?.vat_number ?? '',
            notes: company?.notes ?? '',
            referral_id: company?.referral_id != null ? String(company.referral_id) : '',
            website: company?.website ?? '',
            branch: company?.branch ?? null,
            type: company?.type ?? CompanyBranchType.Branch,
            parent_company_id: company?.parent_company_id ?? null,
            business_category: nextCategory ?? undefined,
            employee_size: nextSize,
            origin: nextOrigin,
            tags: nextTags,
            seller_id: company?.id ? (company.seller_id ?? null) : seller_id,
            first_name: company?.first_name ?? '',
            last_name: company?.last_name ?? '',
            email: company?.email ?? '',
            phone: company?.phone ?? '',
        })

        setBusinessCategory(nextCategory)
        setValue('business_category', nextCategory as string, { shouldValidate: false })
        setValue('employee_size', nextSize as string, { shouldValidate: false })
        setValue('origin', nextOrigin, { shouldValidate: false })
        setValue('tags', nextTags, { shouldValidate: false })
        setValue('seller_id', company?.id ? (company.seller_id ?? null) : seller_id, { shouldValidate: false })
        setValue('type', (company?.type || CompanyBranchType.Branch) as CompanyBranchType, {
            shouldValidate: false,
        })
        setBranchType(company?.type || CompanyBranchType.Branch)
        setParentCompanyQuery('')
        setParentCompany(company?.parent_company || null)
    }, [
        company,
        isSubmitting,
        seller_id,
        setBranchType,
        setBusinessCategory,
        reset,
        setValue,
    ])

    useEffect(() => {
        if (company?.parent_company_id && !company?.parent_company && !parentCompany) {
            dispatch(CompaniesAction.fetchCompanyByIdRaw(company.parent_company_id))
                .then((parentCompanyData) => {
                    setParentCompany(parentCompanyData)
                })
                .catch(() => {
                    setParentCompany(null)
                })
        }
    }, [company?.parent_company, company?.parent_company_id, dispatch, parentCompany])

    useEffect(() => {
        if (!isInEditModal) {
            checkDuplicates(companyName || undefined, cin || undefined, vat || undefined)
        } else {
            setDuplicates([])
        }
    }, [checkDuplicates, cin, companyName, isInEditModal, vat])

    useEffect(() => {
        if (parentCompany) {
            setBranchType(CompanyBranchType.Branch)
            setValue('type', CompanyBranchType.Branch, { shouldValidate: true })
        }
    }, [parentCompany, setBranchType, setValue])

    const isMounted = useRef(false)
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            return
        }

        const autoCategory = getAutoBusinessCategoryForBranchType(branchTypeValue as CompanyBranchType)
        if (autoCategory) {
            setBusinessCategory(autoCategory)
            setValue('business_category', autoCategory, { shouldValidate: false })
        }
    }, [branchTypeValue, setBusinessCategory, setValue])

    useEffect(() => cleanup, [cleanup])

    const onError = useCallback((error: unknown) => {
        console.error('Company basic form validation error', error)
        toast.error(t('Please fix validation errors'))
    }, [t])

    const onSubmit = useCallback(
        async (data: Partial<Company>, shouldClose: boolean = false) => {
            if (isSubmitting) return
            if (branchTypeField.value === CompanyBranchType.Branch && parentCompanyQuery.trim()) {
                setError('parent_company_id', {type: 'validate', message: t('Select or create the parent company before saving.')})
                return
            }

            const submittedValues = getValues()
            setIsSubmitting(true)

            try {
                const normalizedEmployeeSize = normalizeEmployeeSize(data.employee_size ?? null)

                const cleaned: Partial<Company> = pruneEmpty({
                    id: company?.id,
                    name: data.name?.trim(),
                    cin: data.cin?.trim(),
                    vat_number: data.vat_number?.trim(),
                    notes: data.notes?.trim(),
                    referral_id: data.referral_id != null ? String(data.referral_id).trim() || null : null,
                    website: data.website?.toString().trim() || null,
                    parent_company_id: parentCompany?.id,
                    business_category: businessCategoryField.value,
                    type: branchTypeField.value,
                    branch: data.branch,
                    employee_size: normalizedEmployeeSize,
                    origin: normalizeCompanyOrigin(data.origin) || null,
                    tags: Array.isArray(data.tags) ? data.tags : [],
                    seller_id:
                        data.seller_id != null ? Number(data.seller_id) : company?.seller_id ?? null,
                    first_name: trimOrFallback(data.first_name, company?.first_name),
                    last_name: trimOrFallback(data.last_name, company?.last_name),
                    email: trimOrFallback(data.email, company?.email),
                    phone: trimOrFallback(data.phone, company?.phone),
                    billing_info: data.name?.trim() || company?.billing_info,
                }, ['tags', 'seller_id', 'origin'])

                const response = await dispatch(CompaniesAction.fetchCreateOrUpdateCompany(cleaned as Company))

                if (response) {
                    const mergedCompany = mergeCompanyAfterSave(cleaned, response)
                    dispatch(CompaniesAction.setSelectedCompany(mergedCompany))
                    await Promise.resolve(onChange(mergedCompany, shouldClose))
                    // A confirmed save establishes the new baseline for the close guard.
                    // Keep a draft dirty if the user changed it while the request was pending.
                    if (JSON.stringify(getValues()) === JSON.stringify(submittedValues)) {
                        reset(submittedValues)
                    }
                    toast.success(t('Company saved successfully'))
                }
            } catch (error) {
                if (error instanceof CompanySellerAssignmentError) {
                    const message = t('Account saved, but its owner could not be saved. Select the owner and retry.')
                    setError('seller_id', {type: 'server', message}, {shouldFocus: true})
                    toast.error(message)
                } else {
                    toast.error(t('Failed to save company information'))
                }
            } finally {
                setIsSubmitting(false)
            }
        },
        [
            businessCategoryField.value,
            branchTypeField.value,
            company,
            dispatch,
            isSubmitting,
            getValues,
            reset,
            onChange,
            setError,
            parentCompanyQuery,
            parentCompany?.id,
            setIsSubmitting,
            t,
        ]
    )

    const handleOpenMergeModal = useCallback(() => {
        setIsOpeningMergeModal(true)
        dispatch(
            openModal(
                <MergeCompaniesModal
                    duplicates={[...duplicates, ...(company && company.id ? [company] : [])]}
                    initialSearchTerm={companyName || ''}
                    onSuccess={() => {
                        setDuplicates([])
                        if (company?.id) {
                            dispatch(CompaniesAction.fetchCompanyById(company.id))
                        }
                        if (company) {
                            onChange(company, false)
                        }
                    }}
                />,
                { modalSize: ModalSize.RoundedFullscreen },
                t('Merge Companies')
            )
        )

        setTimeout(() => {
            setIsOpeningMergeModal(false)
        }, 1000)
    }, [company, companyName, dispatch, duplicates, onChange, t])

    const saveAndCloseText = getSaveAndCloseLabel(
        showSaveAndClose,
        isSubmitting,
        t('Saving company...'),
        t('Save & Close')
    )

    return {
        form,
        isSubmitting,
        setIsSubmitting,
        showPopover,
        handleMouseEnter,
        handleMouseLeave,
        parentCompanyQuery,
        setParentCompanyQuery,
        parentCompany,
        setParentCompany,
        duplicates,
        isOpeningMergeModal,
        notesFontSize,
        setNotesFontSize,
        businessCategoryField,
        branchTypeField,
        businessCategoryOptionsUi,
        businessBranchTypeOptionsUi,
        employeeSizeOptionsUi,
        handleOpenMergeModal,
        onSubmit,
        onError,
        saveAndCloseText,
        cleanupDuplicateWarning: cleanup,
    }
}
